'use client';
import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Zap, ChevronDown, Share2 } from 'lucide-react';
import ClipsTab from '@/components/ClipsTab';
import SignalsTab from '@/components/SignalsTab';
import ImagesTab from '@/components/ImagesTab';
import WorldCupTab from '@/components/WorldCupTab';
import ConsoleOverlay from '@/components/ConsoleOverlay';
import type { ImageVideo, WorldCupRun } from '@/lib/types';

const supabase = createClient(process.env.NEXT_PUBLIC_SUPABASE_URL!, process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!);

type Tab = 'clips' | 'signals' | 'images' | 'worldcup';

export default function SportsGridPipeline() {
  const [activeTab, setActiveTab] = useState<Tab>('clips');
  const [recaplets, setRecaplets] = useState<any[]>([]);
  const [signals, setSignals] = useState<any[]>([]);
  const [imageVideos, setImageVideos] = useState<ImageVideo[]>([]);
  const [wcVideos, setWcVideos] = useState<ImageVideo[]>([]);
  const [wcRuns, setWcRuns] = useState<WorldCupRun[]>([]);
  const [globalMax, setGlobalMax] = useState(3);
  const [isPipelineRunning, setIsPipelineRunning] = useState(false);
  const [consoleLogs, setConsoleLogs] = useState<string[]>([]);
  const [autoPublish, setAutoPublish] = useState(false);
  const [autoPublishBusy, setAutoPublishBusy] = useState(false);
  const [autoPublishWc, setAutoPublishWc] = useState(false);
  const [autoPublishWcBusy, setAutoPublishWcBusy] = useState(false);
  
  // DATE LOGIC
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  });

  // Format: "2026-03-06" for Supabase filtering
  const formattedForDb = currentDate.toISOString().split('T')[0];
  
  // Format: "MARCH 6, 2026" for UI
  const formattedForUi = currentDate.toLocaleDateString('en-US', { 
    month: 'long', day: 'numeric', year: 'numeric' 
  }).toUpperCase();

  const changeDate = (days: number) => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + days);
    setCurrentDate(next);
  };

const prevTab = useRef(activeTab);

useEffect(() => {
  // 1. Handle Tab Defaults only when SWITCHING tabs
  const todayStr = new Date().toISOString().split('T')[0];
  const currentStr = currentDate.toISOString().split('T')[0];
  const tabChanged = prevTab.current !== activeTab;
  prevTab.current = activeTab;

  if (tabChanged && activeTab === 'clips' && currentStr === todayStr) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    setCurrentDate(yesterday);
    return; // The next useEffect cycle triggered by setCurrentDate will fetch data
  }

  if (tabChanged && activeTab === 'signals' && currentStr !== todayStr) {
    setCurrentDate(new Date());
    return;
  }

  // 2. Actually fetch the data
  fetchData();
}, [activeTab, formattedForDb]); // Keep both! formattedForDb is a string derived from currentDate
  const fetchData = async () => {
    if (activeTab === 'images') {
      // Instant Reaction videos are not date-scoped — show the full history,
      // excluding World Cup videos (those live in their own tab).
      const { data } = await supabase
        .from('image_videos')
        .select('*')
        .neq('category', 'worldcup')
        .order('created_at', { ascending: false });
      setImageVideos(data || []);
    } else if (activeTab === 'worldcup') {
      // World Cup tab: all World Cup videos + all prompt runs (show everything).
      const [{ data: vids }, { data: runs }] = await Promise.all([
        supabase.from('image_videos').select('*').eq('category', 'worldcup').order('created_at', { ascending: false }),
        supabase.from('worldcup_runs').select('*').order('created_at', { ascending: false }),
      ]);
      setWcVideos(vids || []);
      setWcRuns(runs || []);
    } else if (activeTab === 'clips') {
      const { data } = await supabase
        .from('recaplets')
        .select('*')
        .eq('game_date', formattedForDb).gte('clips_downloaded', 1) // ACTUALLY FILTERING NOW
        .order('created_at', { ascending: false });
      setRecaplets(data || []);
    } else {
      // For signals, we check created_at date part
      const { data } = await supabase
        .from('market_signals')
        .select('*')
        .gte('created_at', `${formattedForDb}T00:00:00`)
        .lte('created_at', `${formattedForDb}T23:59:59`)
        .order('created_at', { ascending: false });
      setSignals(data || []);
    }
  };

  const runAgent = async (type: 'clips' | 'signals') => {
    setIsPipelineRunning(true);
    setConsoleLogs([`[${new Date().toLocaleTimeString()}] Triggering ${type} agent...`]);
    try {
      await fetch('/api/run-agent', { method: 'POST', body: JSON.stringify({ type, date: formattedForDb }) });
      setTimeout(() => { setIsPipelineRunning(false); fetchData(); }, 5000);
    } catch (e) {
      setIsPipelineRunning(false);
    }
  };

  const stopAgent = async () => {
    try {
      await fetch('/api/run-agent', { method: 'DELETE' });
    } catch (e) {
      console.error('Failed to stop agent', e);
    } finally {
      setIsPipelineRunning(false);
      setConsoleLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] Agent stopped.`]);
    }
  };

  const getLogo = (alias: string) => `https://d3gys9eb0c01wp.cloudfront.net/SPORTSGRID_APP/team_logos/NBA/${alias}.webp`;

  useEffect(() => {
    fetch('/api/settings')
      .then((r) => r.json())
      .then((d) => {
        setAutoPublish(!!d.auto_publish_social);
        setAutoPublishWc(!!d.auto_publish_worldcup);
      })
      .catch(() => {});
  }, []);

  const toggleAutoPublish = async () => {
    if (autoPublishBusy) return;
    const next = !autoPublish;
    setAutoPublishBusy(true);
    setAutoPublish(next);
    try {
      const res = await fetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_publish_social: next }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setAutoPublish(!next);
    } finally {
      setAutoPublishBusy(false);
    }
  };

  const toggleAutoPublishWc = async () => {
    if (autoPublishWcBusy) return;
    const next = !autoPublishWc;
    setAutoPublishWcBusy(true);
    setAutoPublishWc(next);
    try {
      const res = await fetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_publish_worldcup: next }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setAutoPublishWc(!next);
    } finally {
      setAutoPublishWcBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-slate-300 px-4 py-6 md:p-8 font-sans">
      <div className="flex justify-between items-center mb-8 border-b border-white/10 pb-6">
        <h1 className="text-2xl font-black text-white flex items-center gap-3 tracking-tighter">
          <div className="w-2.5 h-8 bg-red-600 rounded-sm shadow-[0_0_15px_rgba(220,38,38,0.5)]" /> 
          SPORTSGRID <span className="text-slate-500 font-light text-xl">VIDEO PIPELINE</span>
        </h1>
      </div>

      {/* NEW INTEGRATED NAV BAR */}
      <div className="flex flex-wrap gap-3 items-center mb-10 bg-white/[0.03] p-2 rounded-xl border border-white/10">
        {/* TAB GROUP */}
        <nav className="flex items-center gap-1 bg-black/40 p-1 rounded-lg border border-white/5 ml-2">
          <button
            onClick={() => setActiveTab('clips')}
            className={`px-6 py-2 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'clips' ? 'bg-red-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
          >
            Highlights
          </button>
          <button
            onClick={() => setActiveTab('images')}
            className={`px-6 py-2 rounded-md text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === 'images' ? 'bg-red-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
          >
            Instant Reaction
          </button>
          <button
            onClick={() => setActiveTab('worldcup')}
            className={`px-6 py-2 rounded-md text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === 'worldcup' ? 'bg-red-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
          >
            World Cup
          </button>
          <button
            onClick={() => setActiveTab('signals')}
            className={`px-6 py-2 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'signals' ? 'bg-red-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
          >
            Signals
          </button>
        </nav>

        {/* DATE PICKER (only the date-scoped Highlights & Signals tabs) */}
        {(activeTab === 'clips' || activeTab === 'signals') && (
          <div className="flex items-center gap-4 md:border-l md:border-white/10 md:pl-8">
            <button className="text-slate-500 hover:text-white p-1 hover:bg-white/5 rounded" onClick={() => changeDate(-1)}>
              <ChevronDown className="rotate-90" size={18} />
            </button>
            <div className="min-w-[140px] text-center">
              <span className="font-mono font-bold text-sm text-white">{formattedForUi}</span>
            </div>
            <button className="text-slate-500 hover:text-white p-1 hover:bg-white/5 rounded" onClick={() => changeDate(1)}>
              <ChevronDown className="-rotate-90" size={18} />
            </button>
          </div>
        )}

        {/* RIGHT ACTIONS */}
        <div className="flex items-center gap-4 ml-auto mr-2">
          {activeTab === 'clips' && (
            <div className="flex items-center gap-3 bg-black/20 px-4 py-1.5 rounded-lg border border-white/5">
              <span className="text-[9px] font-black text-slate-600 uppercase">Limit</span>
              <select
                value={globalMax}
                onChange={(e) => setGlobalMax(Number(e.target.value))}
                className="bg-transparent text-white font-bold text-xs outline-none cursor-pointer"
              >
                {[1, 2, 3].map(n => <option key={n} value={n} className="bg-black">{n}</option>)}
              </select>
            </div>
          )}

          {/* Highlights & Signals: shared auto-publish + pipeline agent run/stop */}
          {(activeTab === 'clips' || activeTab === 'signals') && (
            <>
              <button
                onClick={toggleAutoPublish}
                disabled={autoPublishBusy}
                title="Auto-publish recaps to Instagram and Facebook Reels ~10 min after each game ends"
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all border ${
                  autoPublish
                    ? 'bg-green-600/90 hover:bg-green-500 text-white border-green-400/30'
                    : 'bg-black/40 hover:bg-black/60 text-slate-400 border-white/10'
                } ${autoPublishBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Share2 size={13} />
                <span>Auto-publish</span>
                <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] ${autoPublish ? 'bg-white/20' : 'bg-white/5'}`}>
                  {autoPublish ? 'ON' : 'OFF'}
                </span>
              </button>

              {isPipelineRunning ? (
                <button
                  onClick={stopAgent}
                  className="group flex items-center gap-3 px-6 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all bg-red-600 hover:bg-red-500 text-white"
                >
                  <Zap size={14} fill="currentColor" /> Stop Agent
                </button>
              ) : (
                <button
                  onClick={() => runAgent(activeTab as 'clips' | 'signals')}
                  className="group flex items-center gap-3 px-6 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all bg-green-600 hover:bg-green-500 text-white"
                >
                  <Zap size={14} fill="currentColor" /> Run {activeTab}
                </button>
              )}
            </>
          )}

          {/* World Cup: its own auto-publish toggle (separate from Highlights) */}
          {activeTab === 'worldcup' && (
            <button
              onClick={toggleAutoPublishWc}
              disabled={autoPublishWcBusy}
              title="Auto-publish World Cup videos to Instagram and Facebook Reels (off by default)"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all border ${
                autoPublishWc
                  ? 'bg-green-600/90 hover:bg-green-500 text-white border-green-400/30'
                  : 'bg-black/40 hover:bg-black/60 text-slate-400 border-white/10'
              } ${autoPublishWcBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Share2 size={13} />
              <span>Auto-publish</span>
              <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] ${autoPublishWc ? 'bg-white/20' : 'bg-white/5'}`}>
                {autoPublishWc ? 'ON' : 'OFF'}
              </span>
            </button>
          )}
        </div>
      </div>

      <div className="bg-white/[0.01] rounded-2xl border border-white/5 overflow-hidden shadow-2xl">
        {activeTab === 'clips' ? (
          <ClipsTab recaplets={recaplets} globalMax={globalMax} getLogo={getLogo} />
        ) : activeTab === 'signals' ? (
          <SignalsTab signals={signals} />
        ) : activeTab === 'worldcup' ? (
          <WorldCupTab videos={wcVideos} onRefresh={fetchData} runs={wcRuns} onRefreshRuns={fetchData} />
        ) : (
          <ImagesTab videos={imageVideos} onRefresh={fetchData} />
        )}
      </div>

      <ConsoleOverlay logs={consoleLogs} isRunning={isPipelineRunning} onBlur={() => setConsoleLogs([])} />
    </div>
  );
}
